/**
 * AI Render panel — captures the viewport and sends it through an
 * image-to-image pipeline on a user-configured OpenAI-compatible endpoint
 * (POST {baseUrl}/images/edits, multipart: image + prompt + model).
 *
 * The API key lives in IndexedDB on this machine only and is sent solely to
 * the endpoint the user typed in. Nothing here talks to any other server.
 */

import type { App } from '../app';
import { getSetting, setSetting } from '@core/storage/settings';

interface AiSettings {
  baseUrl: string;
  apiKey: string;
  model: string;
  prompt: string;
}

const DEFAULTS: AiSettings = {
  baseUrl: 'https://api.openai.com/v1',
  apiKey: '',
  model: 'gpt-image-1',
  prompt:
    'Photorealistic studio product render of this CAD model, brushed aluminum, ' +
    'soft key light, seamless gray backdrop, high detail',
};

export function mountAiPanel(host: HTMLElement, app: App): void {
  const panel = document.createElement('div');
  panel.className = 'nv-side';
  panel.style.display = 'none';
  host.appendChild(panel);

  let settings: AiSettings = { ...DEFAULTS };
  void getSetting<AiSettings>('ai', DEFAULTS).then((s) => {
    settings = { ...DEFAULTS, ...s };
    render();
  });

  let busy = false;
  let statusMsg = '';
  let resultUrl: string | null = null;
  let captureUrl: string | null = null;

  const field = (
    label: string,
    value: string,
    onChange: (v: string) => void,
    opts: { password?: boolean; textarea?: boolean } = {},
  ): HTMLElement => {
    const wrap = document.createElement('div');
    wrap.className = 'field';
    const l = document.createElement('label');
    l.textContent = label;
    const input = opts.textarea
      ? document.createElement('textarea')
      : document.createElement('input');
    if (input instanceof HTMLInputElement) input.type = opts.password ? 'password' : 'text';
    input.value = value;
    input.addEventListener('change', () => {
      onChange(input.value);
      void setSetting('ai', settings);
    });
    wrap.append(l, input);
    return wrap;
  };

  async function runRender(): Promise<void> {
    if (busy) return;
    busy = true;
    statusMsg = 'Capturing viewport…';
    render();
    try {
      captureUrl = await app.viewport.capture(1024);
      statusMsg = 'Uploading to endpoint…';
      render();

      const pngBlob = await (await fetch(captureUrl)).blob();
      const form = new FormData();
      form.append('image', pngBlob, 'viewport.png');
      form.append('prompt', settings.prompt);
      form.append('model', settings.model);
      const base = settings.baseUrl.replace(/\/+$/, '');
      const res = await fetch(`${base}/images/edits`, {
        method: 'POST',
        headers: settings.apiKey ? { Authorization: `Bearer ${settings.apiKey}` } : {},
        body: form,
      });
      if (!res.ok) {
        const text = (await res.text()).slice(0, 200);
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
      }
      const json = (await res.json()) as { data?: { b64_json?: string; url?: string }[] };
      const item = json.data?.[0];
      if (item?.b64_json) resultUrl = `data:image/png;base64,${item.b64_json}`;
      else if (item?.url) resultUrl = item.url;
      else throw new Error('Endpoint returned no image data');
      statusMsg = 'Done';
    } catch (err) {
      statusMsg = `Failed: ${err instanceof Error ? err.message : err}`;
    } finally {
      busy = false;
      render();
    }
  }

  const render = (): void => {
    panel.style.display = app.activePanel === 'ai' ? 'block' : 'none';
    if (app.activePanel !== 'ai') return;
    panel.innerHTML = '<h3>AI Render</h3>';

    panel.appendChild(
      field('Endpoint (OpenAI-compatible)', settings.baseUrl, (v) => (settings.baseUrl = v)),
    );
    panel.appendChild(field('API key', settings.apiKey, (v) => (settings.apiKey = v), { password: true }));
    panel.appendChild(field('Model', settings.model, (v) => (settings.model = v)));
    panel.appendChild(field('Prompt', settings.prompt, (v) => (settings.prompt = v), { textarea: true }));

    const go = document.createElement('button');
    go.className = 'nv-primary';
    go.textContent = busy ? 'Rendering…' : 'Capture & Render';
    go.disabled = busy;
    go.addEventListener('click', () => void runRender());
    panel.appendChild(go);

    if (statusMsg) {
      const s = document.createElement('div');
      s.className = 'side-status';
      s.textContent = statusMsg;
      panel.appendChild(s);
    }
    for (const [label, url] of [
      ['Viewport capture', captureUrl],
      ['AI result', resultUrl],
    ] as const) {
      if (!url) continue;
      const cap = document.createElement('div');
      cap.className = 'side-caption';
      cap.textContent = label;
      const img = document.createElement('img');
      img.src = url;
      img.className = 'side-img';
      panel.append(cap, img);
    }
  };

  app.onUI(render);
  render();
}
